import { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import dexie from '../offlineDB';
import { normalizeTag } from '../utils/normalize';
import { useNavigate } from 'react-router-dom';

// Convert plain-text paragraphs to minimal HTML matching Tiptap's format.
function textToHtml(text) {
  return text
    .split(/\n{2,}/)
    .map(para => para.trim())
    .filter(Boolean)
    .map(para => `<p>${para.replace(/\n/g, '<br>')}</p>`)
    .join('') || '';
}

function capitalize(s) {
  const t = (s || '').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : '';
}

export default function QuickAddNoteModal({ open, onClose, user, genreOptions = [], typeOptions = [], tagOptions = [] }) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [type, setType] = useState('');
  const [body, setBody] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const bodyRef = useRef(null);
  const navigate = useNavigate();

  const resetForm = () => {
    setSavedId(null);
    setTitle('');
    setCategory('');
    setType('');
    setBody('');
    setTagInput('');
  };

  useEffect(() => {
    if (open) {
      resetForm();
      setTimeout(() => bodyRef.current?.focus(), 80);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const handleSave = async () => {
    if (saving) return;
    const bodyHtml = textToHtml(body);
    if (!title.trim() && !bodyHtml) return;

    setSaving(true);
    try {
      const docId = crypto.randomUUID();
      const tags = tagInput
        .split(',')
        .map(t => normalizeTag(t.trim()))
        .filter(Boolean);

      const resolvedTitle = title.trim() ||
        body.trim().split(/\n/)[0].slice(0, 80) ||
        new Date().toLocaleDateString(undefined, { dateStyle: 'medium' });

      const data = {
        title: resolvedTitle,
        mode: 'notes',
        notes: bodyHtml,
        quickNote: '',
        customTags: tags,
        genre: capitalize(category),
        type: capitalize(type),
        key: '',
        status: '',
        rating: '',
        attachments: [],
        recordings: [],
        links: [],
        link1: '', link2: '', link3: '',
        createdAt: navigator.onLine ? serverTimestamp() : new Date().toISOString(),
        updatedAt: navigator.onLine ? serverTimestamp() : new Date().toISOString(),
        lastViewedAt: '',
      };

      if (navigator.onLine) {
        await setDoc(doc(db, 'users', user.uid, 'tunes', docId), data);
      } else {
        await dexie.tunes.put({ id: docId, ...data, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
        await dexie.pendingWrites.put({ type: 'add', collection: 'tunes', docId, data });
      }

      setSavedId(docId);
    } catch (err) {
      console.error('QuickAdd save failed:', err);
      alert('Failed to save note. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const inputStyle = {
    width: '100%',
    background: 'var(--input-bg)',
    border: '1px solid var(--card-border, #ccc)',
    borderRadius: '7px',
    color: 'var(--text-primary)',
    padding: '8px 10px',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit',
  };

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        zIndex: 9998,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderRadius: '12px',
          padding: '1.25rem',
          width: '100%',
          maxWidth: '460px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
        }}
        onClick={e => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {savedId ? (
          /* ── Saved confirmation ── */
          <div style={{ textAlign: 'center', padding: '0.5rem 0 0.25rem' }}>
            <div style={{ fontSize: '1.4rem', marginBottom: '0.5rem' }}>Note saved</div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '1rem' }}>
              <button
                className="btn-subtle"
                onClick={() => navigate(`/tune/${savedId}`)}
                style={{ padding: '8px 16px' }}
              >
                View note
              </button>
              <button
                className="btn-subtle"
                onClick={() => { resetForm(); setTimeout(() => bodyRef.current?.focus(), 60); }}
                style={{ padding: '8px 16px' }}
              >
                Add another
              </button>
              <button
                className="btn-primary"
                onClick={onClose}
                style={{ padding: '8px 16px' }}
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          /* ── Input form ── */
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.9rem' }}>
              <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>Quick note</span>
              <button className="btn-subtle" onClick={onClose} style={{ padding: '3px 9px', fontSize: '1rem' }}>✕</button>
            </div>

            <input
              type="text"
              placeholder="Title (optional)"
              value={title}
              onChange={e => setTitle(e.target.value)}
              style={{ ...inputStyle, marginBottom: '8px' }}
            />

            {/* Category + Type — two-column row with datalist suggestions from actual data */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
              <div>
                <input
                  type="text"
                  list="quick-add-categories"
                  placeholder="Category"
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                  style={inputStyle}
                />
                <datalist id="quick-add-categories">
                  {genreOptions.map(g => <option key={g} value={g} />)}
                </datalist>
              </div>
              <div>
                <input
                  type="text"
                  list="quick-add-types"
                  placeholder="Type"
                  value={type}
                  onChange={e => setType(e.target.value)}
                  style={inputStyle}
                />
                <datalist id="quick-add-types">
                  {typeOptions.map(t => <option key={t} value={t} />)}
                </datalist>
              </div>
            </div>

            <textarea
              ref={bodyRef}
              placeholder="Note…"
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={5}
              style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5, marginBottom: '8px' }}
            />

            {/* Tags with suggestions from all existing tags in the library */}
            <input
              type="text"
              list="quick-add-tags"
              placeholder="Tags (comma-separated)"
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              style={{ ...inputStyle, marginBottom: '1rem' }}
            />
            <datalist id="quick-add-tags">
              {tagOptions.map(t => <option key={t} value={t} />)}
            </datalist>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.75rem', opacity: 0.45 }}>⌘ Enter to save</span>
              <button
                className="btn-primary"
                onClick={handleSave}
                disabled={saving || (!title.trim() && !body.trim())}
                style={{ padding: '8px 20px' }}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
